import { LeadListImportColumn, LeadListImportError, LeadListImportSummary } from '../../models/LeadList';

/**
 * Aviso do resumo do import na tela da lista (CU-17tm466pyc5).
 *
 * Texto: "Importados X contatos. Nome = coluna <rótulo ou nº>, Telefone = coluna <…>,
 * E-mail = coluna <…>. Y linhas ficaram de fora." Y soma as linhas puladas (`skipped`) e as
 * que não entraram por lote falho (`failed`); com `failed` > 0 o texto pede para importar de
 * novo e o aviso vira a variante de atenção, como quando nenhum contato entrou
 * (`importSummaryVariant`). O aviso fica até o cliente fechar;
 * o fechamento é guardado por lista com o `finished_at` do import, então um import
 * novo (outro `finished_at`) volta a mostrar.
 *
 * Planilha recusada (passou do limite de leitura ou não pôde ser lida) vem com `error` e
 * vira a variante de erro (`importSummaryError`), com o mesmo fechar e continuar fechado.
 */
export const IMPORT_SUMMARY_DISMISSED_PREFIX = 'SMSFUNNEL::IMPORT_SUMMARY_DISMISSED::';

const numberFormat = new Intl.NumberFormat('pt-BR');

function formatCount(count: number): string {
  return numberFormat.format(count);
}

/** Valor ausente, negativo ou não numérico vira 0 (não mostra "NaN contatos"). */
function countOf(value: number | null | undefined): number {
  return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
}

/** "coluna Nome completo", "coluna 3" (sem cabeçalho) ou `null` se não há o que mostrar. */
export function describeImportColumn(column: LeadListImportColumn | null | undefined): string | null {
  if (!column) {
    return null;
  }

  const label = typeof column.label === 'string' ? column.label.trim() : '';
  if (label) {
    return `coluna ${label}`;
  }

  if (typeof column.position === 'number' && Number.isFinite(column.position)) {
    return `coluna ${column.position}`;
  }

  return null;
}

/** Linhas que não entraram por lote falho; ausente (resumo antigo) conta como 0. */
export function importSummaryFailedCount(summary: LeadListImportSummary): number {
  return countOf(summary.failed);
}

export function importSummaryHasFailures(summary: LeadListImportSummary): boolean {
  return importSummaryFailedCount(summary) > 0;
}

/** Variante do aviso: cor (`notice--<variante>`) e ícone. */
export type ImportSummaryVariant = 'success' | 'warning' | 'danger';

/** Ícone do aviso por variante. */
export const IMPORT_SUMMARY_ICONS: Record<ImportSummaryVariant, string> = {
  success: 'fa-check-circle',
  warning: 'fa-exclamation-triangle',
  danger: 'fa-times-circle',
};

/**
 * `danger` quando a planilha foi recusada; `warning` com lote falho ou quando nenhum
 * contato entrou (`imported` 0, todas as linhas de fora); `success` no resto.
 */
export function importSummaryVariant(summary: LeadListImportSummary): ImportSummaryVariant {
  if (importSummaryError(summary) !== null) {
    return 'danger';
  }

  if (importSummaryHasFailures(summary) || countOf(summary.imported) === 0) {
    return 'warning';
  }

  return 'success';
}

const IMPORT_ERROR_PREFIX = 'Não foi possível importar o arquivo: ';

/** Texto por código de recusa; o código é o contrato com o backend, não a mensagem. */
export const IMPORT_ERROR_MESSAGES: Record<string, string> = {
  too_large: `${IMPORT_ERROR_PREFIX}a planilha passa do limite de leitura. `
    + 'Divida o arquivo em partes menores ou salve como .csv e importe de novo.',
  unreadable: `${IMPORT_ERROR_PREFIX}o arquivo não pôde ser lido. `
    + 'Confira se ele abre no Excel, salve como .xlsx ou .csv e importe de novo.',
};

/** O motivo da recusa, ou `null` quando o import terminou (com ou sem lote falho). */
export function importSummaryError(summary: LeadListImportSummary): LeadListImportError | null {
  const error = summary.error;
  return error && typeof error === 'object' && typeof error.code === 'string' ? error : null;
}

/**
 * Código conhecido usa o texto da tela; desconhecido usa a mensagem do backend (já escrita
 * para o cliente) e, sem ela, o texto de arquivo ilegível.
 */
export function buildImportErrorMessage(error: LeadListImportError): string {
  if (Object.prototype.hasOwnProperty.call(IMPORT_ERROR_MESSAGES, error.code)) {
    return IMPORT_ERROR_MESSAGES[error.code];
  }

  const message = typeof error.message === 'string' ? error.message.trim() : '';
  return message || IMPORT_ERROR_MESSAGES['unreadable'];
}

export function buildImportSummaryMessage(summary: LeadListImportSummary): string {
  const error = importSummaryError(summary);
  if (error) {
    return buildImportErrorMessage(error);
  }

  const imported = countOf(summary.imported);
  const failed = importSummaryFailedCount(summary);
  const skipped = countOf(summary.skipped) + failed;
  const columns = summary.columns ?? { name: null, phone: null, email: null };

  const importedText = imported === 1
    ? `Importado ${formatCount(imported)} contato.`
    : `Importados ${formatCount(imported)} contatos.`;

  const name = describeImportColumn(columns.name) ?? 'coluna não identificada';
  const phone = describeImportColumn(columns.phone) ?? 'coluna não identificada';
  const email = describeImportColumn(columns.email) ?? 'não identificado';
  const columnsText = `Nome = ${name}, Telefone = ${phone}, E-mail = ${email}.`;

  let skippedText: string;
  if (skipped === 0) {
    skippedText = 'Nenhuma linha ficou de fora.';
  } else if (skipped === 1) {
    skippedText = '1 linha ficou de fora.';
  } else {
    skippedText = `${formatCount(skipped)} linhas ficaram de fora.`;
  }

  if (failed > 0) {
    const failedText = failed === 1
      ? '1 não entrou por erro ao gravar; importe o arquivo de novo para completar.'
      : `${formatCount(failed)} não entraram por erro ao gravar; importe o arquivo de novo para completar.`;
    skippedText = `${skippedText} ${failedText}`;
  }

  return `${importedText} ${columnsText} ${skippedText}`;
}

export function importSummaryDismissedKey(listId: string): string {
  return IMPORT_SUMMARY_DISMISSED_PREFIX + listId;
}

/** Navegador sem localStorage (modo privado restrito) conta como "não fechado". */
export function isImportSummaryDismissed(listId: string, summary: LeadListImportSummary): boolean {
  try {
    return localStorage.getItem(importSummaryDismissedKey(listId)) === summary.finished_at;
  } catch {
    return false;
  }
}

export function dismissImportSummary(listId: string, summary: LeadListImportSummary): void {
  try {
    localStorage.setItem(importSummaryDismissedKey(listId), summary.finished_at);
  } catch {
    // Sem localStorage o aviso só some até recarregar a tela.
  }
}
